import styled from '@emotion/styled'
import { PbsStackOutput } from '@pubstack/common/src/stack'
import { capitalizeFirstLetter } from '@pubstack/common/src/stringUtils'
import { isEqual } from 'lodash-es'
import { FunctionComponent } from 'react'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import Chip from '~/components/Chip'
import { IconProps } from '~/components/Icon'
import { Tooltip } from '~/components/Tooltip'
import { CHANGELOG_ICONS, CHIP_STYLE_BY_ACTION } from './stackChangelogStyles'
import { useBiddersPbsStackChangelog } from './useStackChangelog'
import { TooltipContent } from './ViewStackFlyout'

const SectionTitle = styled.div`
  ${Fonts.P2}
  color: ${Colors.Jet};
  font-weight: 700;
  margin-bottom: 0.25rem;
`

const Flex = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
  align-items: center;
`

type ChangelogChipProps = {
  action: string
  iconLeft: IconProps
  name: string
  adUnitNames?: string[]
  bidAdjustment?: number
}

const ChangelogChip: FunctionComponent<ChangelogChipProps> = ({ action, iconLeft, name, adUnitNames, bidAdjustment }) => {
  return (
    <Tooltip
      key={name}
      offset={8}
      title={
        <TooltipContent css={{ width: 'fit-content', maxWidth: '40rem' }}>
          {adUnitNames && adUnitNames.length > 0 && (
            <div>
              <SectionTitle>{capitalizeFirstLetter(action)} mapping</SectionTitle>
              <Flex>
                {adUnitNames.map((adUnitName) => (
                  <Chip key={adUnitName} text={adUnitName} color={Colors.Ghost} />
                ))}
              </Flex>
            </div>
          )}
          {bidAdjustment !== undefined && (
            <div>
              <SectionTitle>{capitalizeFirstLetter(action)} bid adjustment</SectionTitle>
              <div>{`${(bidAdjustment * 100).toFixed(0)}%`}</div>
            </div>
          )}
        </TooltipContent>
      }
    >
      <Chip iconLeft={iconLeft} text={name} css={CHIP_STYLE_BY_ACTION[action]} />
    </Tooltip>
  )
}
type Props = {
  output1: PbsStackOutput | undefined
  output2: PbsStackOutput | undefined
}

export const PbsStackChangelog: FunctionComponent<Props> = ({ output1, output2 }) => {
  const { changelog } = useBiddersPbsStackChangelog(output1, output2)

  return (
    <>
      <Flex>
        {Object.entries(changelog)
          .filter(([, value]) => Object.keys(value).length > 0)
          .map(([key, value]) =>
            Object.entries(value).map(([bidderName, { adUnitNames, bidAdjustment }]) => (
              <ChangelogChip key={bidderName} action={key} iconLeft={CHANGELOG_ICONS[key]} name={bidderName} adUnitNames={adUnitNames} bidAdjustment={bidAdjustment} />
            ))
          )}
      </Flex>
      {!isEqual(output1?.floors, output2?.floors) && <Chip iconLeft={CHANGELOG_ICONS['changed']} text="Floor prices" css={CHIP_STYLE_BY_ACTION['changed']} />}
    </>
  )
}
